import React, { useState, useRef, useEffect } from "react";
import { authFetch, useAuthFetch } from "./AuthProvider";

// PRODUÇÃO
const API_BASE = "https://apitigdig13.biti9.ai/api";
// DEV local: "http://localhost:4000/api" (rotas sob o prefixo /api — ver APIRouter(prefix="/api/trct") em trct.py)


export default function UploadTRCT() {
  const authFetch = useAuthFetch();

  // Estados de arquivo e parâmetros configuráveis
  const [file, setFile] = useState(null);
  const [labelSubpasta, setLabelSubpasta] = useState("11 Nome");
  const [chaveForte, setChaveForte] = useState("TRCT");

  // Estados de controle de execução
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState("idle"); 
  const [progress, setProgress] = useState({ processed: 0, total: 0 });
  const [log, setLog] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [zipData, setZipData] = useState(null);

  // Referências
  const pollRef = useRef(null);
  const logEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Auto-scroll no container de Logs quando chega um item novo
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [log]);

  // Cancelamento de segurança caso o componente seja desmontado durante o processamento
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  const pararPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  const handleReset = () => {
    setFile(null);
    setJobId(null);
    setStatus("idle");
    setProgress({ processed: 0, total: 0 });
    setLog([]);
    setErrorMsg("");
    setZipData(null);
    setChaveForte('TRCT');
    setLabelSubpasta('11 Nome')
    
    if (fileInputRef.current){
      fileInputRef.current.value="";
    }
  };

  const handleProcessarLote = async () => {
    if (!file) {
      setErrorMsg("Selecione o arquivo TRCT em lote antes de processar.");
      return;
    }

    setJobId(null);
    setProgress({ processed: 0, total: 0 });
    setLog([]);
    setErrorMsg("");
    setZipData(null);
    
    setStatus("uploading");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("labelSubpasta", labelSubpasta);
      formData.append("chaveForte", chaveForte);

      const res = await authFetch(`${API_BASE}/trct/process`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        let errDetail = "Falha ao iniciar o processamento do lote.";
        try {
          const errData = await res.json();
          if (errData.detail) errDetail = errData.detail;
        } catch (_) {}
        throw new Error(errDetail);
      }

      const { jobId: novoJobId } = await res.json();
      setJobId(novoJobId);
      setStatus("processing");

      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await authFetch(`${API_BASE}/trct/status/${novoJobId}`);
          if (!statusRes.ok) {
            throw new Error("Falha ao consultar o status do processamento.");
          }

          const dados = await statusRes.json();

          if (dados.status) setStatus(dados.status);
          if (dados.processed !== undefined && dados.total !== undefined) {
            setProgress({ processed: dados.processed, total: dados.total });
          }
          if (dados.log) setLog(dados.log);
          if (dados.status === "error" && dados.message) setErrorMsg(dados.message);

          const statusesFinais = ["Completo", "Arquivo com falha de leitura.", "error"];
          const finalizado = statusesFinais.includes(dados.status) || (dados.status || "").startsWith("O arquivo");

          if (finalizado) {
            pararPolling();

            if (dados.zipBase64) {
              setZipData(dados.zipBase64);
              fazerDownloadDoZip(dados.zipBase64);
            }
          }
        } catch (pollErr) {
          pararPolling();
          setErrorMsg(pollErr.message);
          setStatus("error");
        }
      }, 1500);
    } catch (err) {
      if (err.name !== "AbortError") {
        setErrorMsg(err.message);
        setStatus("error");
      }
    }
  };

  const handlePausarFila = async () => {
    if (!jobId) return;
    try {
      // Interrompe apenas o polling no front — o processamento em andamento
      // no backend não é cancelado (mesma limitação que já existia antes).
      pararPolling();
      setStatus("paused");
    } catch (err) {
      setErrorMsg("Não foi possível sinalizar a pausa da fila. Tente novamente.");
    }
  };

  const handleDownload = () => {
    if (!zipData) {
      setErrorMsg("Nenhum dado de download disponível. Tente processar novamente.");
      return;
    }
    fazerDownloadDoZip(zipData);
  };

  const fazerDownloadDoZip = (base64Data) => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/zip" });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const nomeFormatado = chaveForte.replace(/\s+/g, "_");
      a.download = `${nomeFormatado}_Arquivo separado.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErrorMsg("Erro ao processar o download do ZIP: " + err.message);
    }
  };

  const isProcessing = ["idle", "Completo", "cancelled", "paused", "error", "validation_error"].includes(status) == false 
  const isFinished = status == 'validation_error' || status === "Completo" || status === "cancelled" || status === "paused" || status === "error" || status.startsWith('O arquivo');
  const canDownload = isFinished && zipData;

  const sucessos = log.filter((item) => item.status === "Sucesso" || item.status === "sucesso").length;
  const falhas = log.length - sucessos;

  return (
    <div className="min-h-screen bg-slate-50 pt-2 pb-6 px-4">
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-start mb-2">
        <img src="logo-tigre.png" alt="logo-tigre" className="w-56" />
      </div>
      
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-md p-8">
        {/* Header centralizado */}
        <header className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-slate-800">
            Separação de arquivo
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Fazer upload de apenas 1 arquivo que deseja para separação rápida em tempo real.
          </p>
        </header>

        {/* Upload do arquivo TRCT Customizado */}
        <div className="max-w-md mx-auto text-center mb-5">
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Selecione o arquivo no botão abaixo. 
          </label>
          <div className="flex flex-col items-center justify-center gap-2">
            <label className={`cursor-pointer bg-blue-50 text-blue-700 hover:bg-blue-100 font-medium text-sm px-4 py-2 rounded-lg transition inline-flex items-center gap-2 shadow-sm ${isProcessing ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}>
              <span>Escolher Arquivo</span>
              <input
                type="file"
                accept=".pdf, .xlsx, .xls"
                onChange={handleFileChange}
                disabled={isProcessing}
                className="hidden"
                ref={fileInputRef}
              />
            </label>
            <span className="text-xs text-slate-500 truncate max-w-full">
              {file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)` : "Nenhum arquivo escolhido"}
            </span>
          </div>
        </div>

        {/* Parâmetros fixos centralizados */}
        <div className="max-w-md mx-auto text-center mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Informe qual arquivo você vai subir.
          </label>
          <select
            value={chaveForte}
            onChange={(e) => setChaveForte(e.target.value)}
            disabled={isProcessing}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-center
            disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
          >
            <option value="" disabled>Selecione...</option>
            <option value="TRCT">TRCT</option>
            <option value="Holerite">Holerite</option>
            <option value="Seguro Desemprego">Seguro desemprego</option>
            <option value="Extrato FGTS">Extrato FGTS</option>
          </select>
          <p className="text-xs text-slate-400 mt-1">
            Identificador crítico para determinar o corte seguro do PDF de acordo com o arquivo.
          </p>
        </div>

        {/* Ações principais */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={handleProcessarLote}
            disabled={isProcessing || !file}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300
                      text-white font-medium py-2.5 rounded-lg transition shadow-sm"
          >
            {isProcessing ? "Processando..." : "Processar Lote"}
          </button>
          
        </div>

        {/* Progresso */}
        {status !== "idle" && (
          <div className="mb-6">
            <div className="flex justify-between text-sm text-slate-600 mb-1">
              <span>Status: {statusLabel(status)}</span>
              <span>
                {progress.processed}/{progress.total || "?"}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-blue-500 h-2.5 rounded-full transition-all duration-300"
                style={{
                  width: progress.total
                    ? `${(progress.processed / progress.total) * 100}%`
                    : "0%",
                }}
              />
            </div>
          </div>
        )}

        {errorMsg && (
          <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            {errorMsg}
          </div>
        )}

        {/* Log de processamento em tempo real */}
        {log.length > 0 && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-semibold text-slate-700">
                Log de Processamento
              </h2>
              <div className="flex gap-2 text-xs">
                <span className="text-emerald-600 font-medium">{sucessos} com sucesso</span>
                <span className="text-slate-300">|</span>
                <span className="text-red-600 font-medium">{falhas} com erro/ignorado</span>
              </div>
            </div>
            <ul className="max-h-56 overflow-y-auto text-xs divide-y divide-slate-100 border border-slate-200 rounded-lg">
              {log.map((entry, idx) => (
                <li key={idx} className="px-3 py-1.5 flex justify-between items-center">
                  <span className="text-slate-600 font-medium">{entry.nome}</span>
                  <span className={statusBadgeClass(entry.status)}>{entry.status}</span>
                </li>
              ))}
              <div ref={logEndRef} />
            </ul>
          </div>
        )}

        {/* Ações pós-processamento */}
        <div className="flex flex-col gap-3">
          {canDownload && (
            <button
              onClick={handleDownload}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 rounded-lg transition shadow-sm"
            >
              Baixar .zip (PDFs + LOG)
            </button>
          )}

          {isFinished && (
            <button
              onClick={handleReset}
              className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 rounded-lg transition"
            >
              Limpar / Novo Processamento
            </button>
          )}
        </div>
      </div>
      <img
        src="logo-biti9.png"
        alt="logo-biti9"
        className="fixed bottom-4 right-4 w-24"
      />
    </div>
  );
}

function statusLabel(status) {
  const map = {
    idle: "Aguardando",
    uploading: "Enviando arquivo...",
    ocr: "Lendo arquivo (OCR)...",
    processing: "Processando lote...",
    paused: "Pausando (finalizando item atual)...",
    completed: "Concluído",
    cancelled: "Interrompido pelo usuário",
    error: "Erro",
  };
  return map[status] || status;
}

function statusBadgeClass(itemStatus) {
  const base = "text-[11px] font-medium px-2 py-0.5 rounded-full";
  const normalized = itemStatus?.toLowerCase();

  if (normalized === "sucesso") {
    return `${base} bg-emerald-100 text-emerald-700`;
  }
  if (normalized === "homônimo ignorado" || normalized === "homonimo ignorado") {
    return `${base} bg-amber-100 text-amber-700`;
  }
  return `${base} bg-red-100 text-red-700`;
}